import {
  ApplicationRef,
  Component,
  ComponentRef,
  computed,
  createComponent,
  inject,
  Injectable,
  input,
  inputBinding,
  Pipe,
  PipeTransform
} from "@angular/core";
import { isFunction } from "lodash-es";
import { toast } from "src/app/libs/snt-sonner/state";
import notify from "devextreme/ui/notify";
import hideToasts from "devextreme/ui/toast/hide_toasts";
import { isString } from "es-toolkit";

import { TranslocoModule } from "@jsverse/transloco";
import { DxButtonModule } from "devextreme-angular";

export type ToastOptions = {
  duration?: number;
  permanent?: boolean;
  sticky?: boolean;

  onDismiss?: () => void;
};

export type ToastConfiguration = {
  title?: string;
  message?: string;
  link?: string;
  linkText?: string;
  image?: string;
  sticky?: boolean;
  action?: {
    label: string;
    action: () => void;
  };
  displayTime?: number;
  onDismiss?: () => void;
};

@Pipe({
  name: "replaceV2",
  standalone: true
})
export class ReplacePipeV2 implements PipeTransform {
  transform(value: string, searchValue: string, replaceValue: string): string {
    return value.replaceAll(searchValue, replaceValue);
  }
}

@Component({
  standalone: true,
  imports: [TranslocoModule, DxButtonModule, ReplacePipeV2],
  selector: "snt-toast-content",
  styles: `
    :host {
      width: 100%;
      display: flex;
    }
  `,
  template: `
    <div class="flex-1 flex flex-row items-start gap-2">
      <div class="flex-1 flex flex-col gap-2">
        @if (title()) {
          <div class="text-sm {{ labelColorClass() }} whitespace-pre-line">
            <span>{{ title() | transloco | replaceV2: "." : ".\\n" }}</span>
          </div>
        }

        @if (message()) {
          <div class="text-xs {{ labelColorClass() }} italic">
            <span>{{ message() | transloco }}</span>
          </div>
        }

        @if (image()) {
          <div>
            <img [src]="image()" class="rounded-md" />
          </div>
        }

        @if (link()) {
          <div class="text-xs {{ labelColorClass() }} italic">
            <span
              class="underline cursor-pointer select-none"
              (click)="open()"
              >{{ linkText() | transloco }}</span
            >
          </div>
        }

        @if (action()) {
          <div class="ml-auto flex flex-row items-center gap-2">
            <dx-button
              [text]="action()!.label"
              [stylingMode]="'text'"
              (onClick)="action()!.action()"
            ></dx-button>
          </div>
        }
      </div>

      <div class="flex flex-col">
        @if (sticky()) {
          <i
            class="fa-light fa-xmark {{ labelColorClass() }} cursor-pointer"
          ></i>
        }
      </div>
    </div>
  `
})
class ToastContentComponent {
  title = input<string | undefined>(undefined);
  message = input<string | undefined>(undefined);
  link = input<string | undefined>(undefined);
  linkText = input<string | undefined>(undefined);
  image = input<string | undefined>(undefined);
  action = input<{ label: string; action: () => void } | undefined>(undefined);
  type = input<"warning" | "error" | "success" | "info" | undefined>(undefined);
  sticky = input<boolean>(false);

  labelColorClass = computed<string>(() => {
    switch (this.type()) {
      case "warning":
        return "text-slate-900";
      default:
        return "text-rose-50";
    }
  });

  open(): void {
    window.open(this.link(), "_blank");
  }
}

@Injectable()
export class ToastService {
  static instance: ToastService;

  readonly #applicationRef = inject(ApplicationRef);

  constructor() {
    ToastService.instance = this;
  }

  clear(): void {
    hideToasts();
  }

  warning(message: string | ToastConfiguration, options?: ToastOptions): void {
    this.#show({ type: "warning", message, options });
  }

  error(message: string | ToastConfiguration, options?: ToastOptions): void {
    this.#show({ type: "error", message, options });
  }

  success(message: string | ToastConfiguration, options?: ToastOptions): void {
    this.#show({ type: "success", message, options });
  }

  info(message: string | ToastConfiguration, options?: ToastOptions): void {
    this.#show({ type: "success", message, options });
  }

  #show({
    type,
    message,
    options
  }: {
    type: "warning" | "error" | "success" | "info";
    message: string | ToastConfiguration;
    options?: ToastOptions;
  }): void {
    switch (true) {
      case isString(message): {
        notify(
          {
            displayTime: 3000,
            width: 384,
            minWidth: 256,
            onHidden: () => {
              if (options?.onDismiss) {
                options.onDismiss();
                return;
              }
            },
            message,
            type
          },
          {
            direction: "up-push",
            position: "bottom left"
          }
        );
        break;
      }
      default: {
        let componentRef: ComponentRef<ToastContentComponent> | undefined;

        notify(
          {
            closeOnClick: !!message?.sticky,
            onHidden: () => {
              const onDismiss = options?.onDismiss ?? message?.onDismiss;

              if (!!onDismiss) {
                onDismiss();
                return;
              }
            },
            width: 384,
            minWidth: 256,
            sticky: message.sticky,
            displayTime: message.sticky
              ? 1000 * 60 * 60 * 24
              : message.displayTime || 5000,
            onDismiss: () => {
              componentRef?.destroy();
              if (message.onDismiss) {
                message.onDismiss();
              }
            },
            type,
            hideOnOutsideClick: false,
            contentTemplate: (container: HTMLElement) => {
              componentRef = createComponent(ToastContentComponent, {
                environmentInjector: this.#applicationRef.injector,
                hostElement: container,
                bindings: [
                  inputBinding("type", () => type),
                  inputBinding("title", () => message.title),
                  inputBinding("message", () => message.message),
                  inputBinding("action", () => message.action),
                  inputBinding("link", () => message.link),
                  inputBinding("linkText", () => message.linkText),
                  inputBinding("image", () => message.image),
                  inputBinding("sticky", () => !!message.sticky)
                ]
              });

              componentRef.changeDetectorRef.detectChanges();
            }
          },
          {
            direction: "up-push",
            position: "bottom left"
          }
        );
        break;
      }
    }
  }

  async observe<T>(
    asyncTaskFn: () => Promise<T>,
    config: {
      loading: string;
      error: string | ((error: any) => string | null | false);
      success: string | ((result: T) => string);
      options?: ToastOptions;
    }
  ): Promise<void> {
    return new Promise<void>((res, rej) => {
      toast.promise(
        async () => {
          try {
            const result = await asyncTaskFn();
            res();
            return result;
          } catch (ex) {
            rej(ex);
            throw ex;
          }
        },
        {
          loading: config.loading,
          success: (d: any) =>
            isFunction(config.success) ? config.success(d) : config.success,
          error: (error: any) =>
            isFunction(config.error) ? config.error(error) : config.error
        }
      );
    });
  }
}
